import { ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import {
  AiChatTurn,
  AiExistingParagraphContext,
  AiFormCatalogSnapshot,
  AiGenerateFormDraftRequest,
  AiGenerateFormDraftResponse,
  AiGeneratedFormDraft
} from '../models/ai-form-draft.model';
import {
  AI_PROVIDER_UNAVAILABLE_WARNING,
  AI_UNAVAILABLE_MESSAGE,
  CONTENT_LOSS_WARNING_MARKERS,
  describeAiError,
  FormsBuilderPolishedService
} from '../services/forms-builder-polished.service';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';

interface AiStudioDialogData {
  // 'pdf' shows the PDF upload step first; 'chat' goes straight to the prompt.
  entry: 'pdf' | 'chat';
  catalog: AiFormCatalogSnapshot;
  existingFormContext?: {
    formName?: string;
    version?: number;
    revision?: number;
  };
  // Deep clone of the live form version — the Studio's initial working copy.
  baseVersion: FormVersionConfigurationModel;
  // Applies a draft to a clone of `base` using server GUIDs, returning the updated clone, the GUIDs
  // of paragraphs that were added/replaced, and any warnings the apply itself raised (field ID
  // naming collisions, unlinkable Note Builder answers). Never mutates `base`.
  applyDraftToWorkingVersion: (
    base: FormVersionConfigurationModel,
    draft: AiGeneratedFormDraft,
    mode: 'pdf' | 'edit'
  ) => Promise<{
    version: FormVersionConfigurationModel;
    changedIds: string[];
    applyWarnings: string[];
  }>;
  // Snapshots a version's paragraphs (index/name/itemLabels) for edit-mode AI context.
  snapshotParagraphs: (version: FormVersionConfigurationModel) => AiExistingParagraphContext[];
  // Form-finder entry: a banner tells the user a new version is created + opened automatically
  // on accept, and the Accept button is relabeled accordingly. The dialog itself changes nothing
  // else — version creation happens in the caller's afterClosed handler.
  autoVersion?: boolean;
  // Display label for the version that will be created (e.g. "v4.1"), shown in the banner.
  autoVersionLabel?: string;
}

/**
 * Unified "AI Form Studio": a two-pane dialog with a conversational editor on the left and a live
 * form preview on the right. Content can originate from a PDF import or a natural-language prompt;
 * every AI turn is applied to an in-memory working copy so the preview updates immediately and the
 * next turn refines the accumulated result. Nothing touches the live form until the user accepts —
 * at which point the whole working copy is returned to the parent to commit in one swap.
 */
@Component({
  standalone: false,
  selector: 'app-forms-builder-ai-studio-dialog',
  templateUrl: './forms-builder-ai-studio-dialog.component.html',
  styleUrls: ['./forms-builder-ai-studio-dialog.component.scss']
})
export class FormsBuilderAiStudioDialogComponent implements OnDestroy {
  // The evolving working copy of the form. Never mutated in place — each turn reassigns it to a
  // fresh clone, and the previous reference is kept on the undo stack.
  workingVersion: FormVersionConfigurationModel;
  // Separate reference fed to the embedded preview so its ngOnChanges fires on every turn.
  previewVersion: FormVersionConfigurationModel | null = null;
  // Snapshots of workingVersion captured before each turn, for undo-last.
  private undoStack: FormVersionConfigurationModel[] = [];
  // Last draft that was applied — handed back on accept for apply telemetry.
  private lastAppliedDraft: AiGeneratedFormDraft | null = null;
  // Active AI draft-generation subscription. generateAiFormDraft polls for up to ~4 minutes, so it
  // must be torn down on destroy — otherwise closing the dialog mid-turn leaves polling running and
  // later fires detectChanges() on a destroyed view.
  private draftSub: Subscription | null = null;
  // Set in ngOnDestroy so async continuations that were already in flight don't touch a dead view.
  private destroyed = false;

  prompt: string = '';
  instructions: string = '';
  noteBuilderFormType: string = '';
  isLoading: boolean = false;
  errorMessage: string = '';
  // Neutral, non-error feedback (currently only "generation stopped"), styled distinctly from
  // errorMessage so a deliberate user action doesn't read as a failure.
  noticeMessage: string = '';

  // Chat transcript (user requests + short assistant replies) shown in the panel.
  turns: { role: 'user' | 'assistant'; text: string }[] = [];
  // Model-facing history: assistant turns carry the draft JSON so the AI refines the same artifact.
  private conversationHistory: AiChatTurn[] = [];

  // Document upload state (used only for the 'pdf' entry step, which takes PDF or Word).
  selectedFile: File | null = null;
  fileBase64: string | null = null;

  // Backend review notes for the most recent turn: `warnings` covers everything the server had to
  // adjust or could not honour (over-20-page truncation, unknown control types mapped to a fallback,
  // PHI-shaped values scrubbed out), and `unmappedTerms` lists the individual terms it couldn't
  // resolve. Both were previously computed and discarded, so a partial import looked complete.
  reviewWarnings: string[] = [];
  reviewUnmappedTerms: string[] = [];
  reviewNotesExpanded: boolean = true;

  // Change-highlight strip over the preview after a turn completes.
  changedParagraphNames: string[] = [];
  deletedCount: number = 0;
  highlightActive: boolean = false;
  private highlightTimer: ReturnType<typeof setTimeout> | null = null;

  progressMessage: string = '';
  elapsedSeconds: number = 0;
  private progressInterval: ReturnType<typeof setInterval> | null = null;

  // Stages must keep advancing for the whole of the service's POLL_TIMEOUT_MS (480s). A multi-page
  // PDF is processed in waves of 3 pages, so a long import legitimately runs for many minutes — if
  // the message stops changing the user assumes it has hung. The last stage therefore has to sit
  // close to the timeout, not at the point where a typical import finishes.
  private static readonly PDF_PROGRESS_STAGES = [
    { afterSeconds: 0,  message: 'Uploading document to AI...' },
    { afterSeconds: 8,  message: 'Analyzing document content...' },
    { afterSeconds: 20, message: 'Generating form structure...' },
    { afterSeconds: 40, message: 'Composing controls and paragraphs...' },
    { afterSeconds: 70, message: 'Finalizing draft...' },
    { afterSeconds: 100, message: 'Still working — longer documents take a few minutes...' },
    { afterSeconds: 150, message: 'Processing the remaining pages...' },
    { afterSeconds: 210, message: 'Still going — long documents are processed a few pages at a time...' },
    { afterSeconds: 270, message: 'Working through the later pages...' },
    { afterSeconds: 330, message: 'Almost there — assembling the final draft...' },
    { afterSeconds: 400, message: 'Wrapping up, hang tight...' },
  ];

  private static readonly EDIT_PROGRESS_STAGES = [
    { afterSeconds: 0,  message: 'Sending request to AI...' },
    { afterSeconds: 8,  message: 'Analyzing form structure...' },
    { afterSeconds: 20, message: 'Generating new content...' },
    { afterSeconds: 45, message: 'Finalizing edits...' },
    { afterSeconds: 80, message: 'Still working, this can take a minute...' },
    { afterSeconds: 140, message: 'Larger edits take a little longer...' },
    { afterSeconds: 210, message: 'Still going — bigger forms take more passes...' },
    { afterSeconds: 300, message: 'Almost there — assembling the final draft...' },
    { afterSeconds: 400, message: 'Wrapping up, hang tight...' },
  ];

  // One size limit for every supported format.
  private static readonly MAX_FILE_BYTES = 10 * 1024 * 1024;

  // Mirrors FormAiMaxPdfPages in the API's Web.config. The server splits the PDF (or cuts a Word
  // document into page-sized sections) and extracts at most this many, then truncates rather than
  // failing, so a long document still returns a usable draft built from its first 20 pages. Nothing
  // here enforces it — the value exists so the upload card can state the limit before the user
  // commits to a multi-minute import, instead of leaving them to find the truncation notice in the
  // review notes afterwards.
  private static readonly MAX_PDF_PAGES = 20;

  // Supported uploads. The extension is checked as well as the MIME type because browsers report
  // an empty file.type for .docx on machines without Office. This is a convenience filter only —
  // the server identifies the real format from the file's bytes and rejects mislabeled files.
  private static readonly SUPPORTED_FILE_TYPES = [
    { extension: '.pdf', mime: 'application/pdf' },
    { extension: '.docx', mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' }
  ];
  private static readonly LEGACY_DOC_MIME = 'application/msword';

  // Legacy .doc stays selectable so choosing one explains why it can't be used, instead of the file
  // simply not appearing in the picker.
  readonly acceptedFileTypes = [
    ...FormsBuilderAiStudioDialogComponent.SUPPORTED_FILE_TYPES.flatMap(t => [t.extension, t.mime]),
    '.doc',
    FormsBuilderAiStudioDialogComponent.LEGACY_DOC_MIME
  ].join(',');

  // Template-facing copies of the two upload limits, so the numbers are declared once here and
  // interpolated into both the upload hint and the rejection message.
  readonly maxFileMb = FormsBuilderAiStudioDialogComponent.MAX_FILE_BYTES / (1024 * 1024);
  readonly maxPdfPages = FormsBuilderAiStudioDialogComponent.MAX_PDF_PAGES;

  constructor(
    private readonly builderService: FormsBuilderPolishedService,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialogRef: MatDialogRef<FormsBuilderAiStudioDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AiStudioDialogData
  ) {
    this.workingVersion = this.data.baseVersion;
    this.previewVersion = JSON.parse(JSON.stringify(this.data.baseVersion));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.draftSub?.unsubscribe();
    this.draftSub = null;
    this.stopProgress();
    if (this.highlightTimer !== null) {
      clearTimeout(this.highlightTimer);
      this.highlightTimer = null;
    }
  }

  // True once the working form has at least one non-deleted paragraph.
  get hasContent(): boolean {
    return (this.workingVersion?.cfgDFormParagraphs || []).some(p => !p.xauditDeleted);
  }

  get showPdfCard(): boolean {
    return this.data.entry === 'pdf' && this.turns.length === 0;
  }

  get showChatIntro(): boolean {
    return this.data.entry === 'chat' && this.turns.length === 0;
  }

  get composerVisible(): boolean {
    return !this.showPdfCard;
  }

  /**
   * The Note Builder form type is only demanded while its select is on screen. Both the chat
   * intro and the PDF card are gated on turns.length === 0, so once the first turn is recorded
   * the select is gone — requiring it past that point would disable Send with no way to satisfy
   * it. It is NOT keyed off hasContent: editing a form that already has paragraphs starts with
   * hasContent true, which is exactly the case that used to let the user skip the selection.
   */
  get formTypeRequired(): boolean {
    return this.turns.length === 0;
  }

  get canUndo(): boolean {
    return !this.isLoading && this.undoStack.length > 0;
  }

  get existingParagraphs(): AiExistingParagraphContext[] {
    return this.data.snapshotParagraphs(this.workingVersion);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    // Reset on every pick, not only on rejection: the browser fires (change) only when the value
    // changes, so choosing the same file again — after it was rejected, or after removing it with
    // the X — would otherwise do nothing at all.
    if (input) { input.value = ''; }
    if (!file) return;

    const name = file.name.toLowerCase();
    if (name.endsWith('.doc') || file.type === FormsBuilderAiStudioDialogComponent.LEGACY_DOC_MIME) {
      this.errorMessage = 'Legacy Word (.doc) files aren\'t supported. Open the file in Word, save it as .docx or PDF, and import that.';
      this.clearFile();
      return;
    }
    const supported = FormsBuilderAiStudioDialogComponent.SUPPORTED_FILE_TYPES
      .some(t => name.endsWith(t.extension) || file.type === t.mime);
    if (!supported) {
      this.errorMessage = 'Only PDF and Word (.docx) files are supported.';
      this.clearFile();
      return;
    }
    if (file.size > FormsBuilderAiStudioDialogComponent.MAX_FILE_BYTES) {
      this.errorMessage = `File is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Please upload a file under ${this.maxFileMb} MB.`;
      this.clearFile();
      return;
    }
    this.errorMessage = '';
    this.selectedFile = file;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      this.fileBase64 = result.split(',')[1] ?? null;
      this.cdr.detectChanges();
    };
    reader.readAsDataURL(file);
  }

  clearFile(): void {
    this.selectedFile = null;
    this.fileBase64 = null;
  }

  // Kicks off the initial document import turn (replace-all).
  importPdf(): void {
    if (this.isLoading) return;
    if (!this.fileBase64) {
      this.errorMessage = 'Please choose a file to import.';
      return;
    }

    if (!this.noteBuilderFormType) {
      this.errorMessage = 'Select a Note Builder form type before importing.';
      return;
    }

    const instructions = (this.instructions || '').trim();
    const request: AiGenerateFormDraftRequest = {
      prompt: instructions,
      catalog: this.data.catalog,
      existingFormContext: this.data.existingFormContext,
      // The API field is named for PDF but carries either format; the server sniffs which.
      pdfBase64: this.fileBase64,
      pdfFileName: this.selectedFile?.name ?? undefined,
      mode: 'pdf',
      noteBuilderFormType: this.noteBuilderFormType || ''
    };

    const label = instructions
      ? `Import "${this.selectedFile?.name}" — ${instructions}`
      : `Import "${this.selectedFile?.name}"`;
    this.runTurn(request, 'pdf', label);
  }

  // Sends a conversational edit against the current working copy.
  sendMessage(): void {
    if (this.isLoading) return;

    const cleanPrompt = (this.prompt || '').trim();
    if (!cleanPrompt) {
      this.errorMessage = this.hasContent
        ? 'Describe the change you want to make.'
        : 'Describe the form you want to build.';
      return;
    }

    if (this.formTypeRequired && !this.noteBuilderFormType) {
      this.errorMessage = 'Select a Note Builder form type before continuing.';
      return;
    }

    const request: AiGenerateFormDraftRequest = {
      prompt: cleanPrompt,
      catalog: this.data.catalog,
      existingFormContext: this.data.existingFormContext,
      mode: 'edit',
      existingParagraphs: this.data.snapshotParagraphs(this.workingVersion),
      noteBuilderFormType: this.noteBuilderFormType || '',
      conversationHistory: this.conversationHistory
    };

    this.prompt = '';
    this.runTurn(request, 'edit', cleanPrompt);
  }

  // Shared turn runner: records the user turn, calls the AI, applies the result to the working
  // copy, refreshes the preview, and threads the draft back into the conversation history.
  private runTurn(request: AiGenerateFormDraftRequest, mode: 'pdf' | 'edit', userText: string): void {
    this.errorMessage = '';
    this.noticeMessage = '';
    this.clearReviewNotes();
    this.turns.push({ role: 'user', text: userText });
    this.conversationHistory.push({ role: 'user', text: userText });

    this.isLoading = true;
    this.startProgress(mode);

    // Cancel any prior in-flight turn before starting a new one, then track this one for teardown.
    this.draftSub?.unsubscribe();
    this.draftSub = this.builderService.generateAiFormDraft(request).subscribe(
      async (response: AiGenerateFormDraftResponse) => {
        if (this.destroyed) return;
        this.stopProgress();

        // The AI needs more detail before it can change anything. That's a normal chat turn, not a
        // failure: show the question and leave the working version untouched until the user answers.
        const clarification = (response?.needsClarification && (response?.assistantMessage || '').trim()) || '';
        if (clarification) {
          this.isLoading = false;
          this.conversationHistory.push({ role: 'assistant', text: clarification });
          this.turns.push({ role: 'assistant', text: clarification });
          this.cdr.detectChanges();
          return;
        }

        if (!response?.success || !response?.draft) {
          this.isLoading = false;
          this.errorMessage = response?.errorMessage || 'The AI could not generate a form. Try rephrasing.';
          this.failTurn(this.errorMessage);
          this.cdr.detectChanges();
          return;
        }

        // The AI provider was down and the backend returned a deterministic placeholder instead of a
        // real draft. Don't apply that placeholder — tell the user the AI is unavailable and to retry.
        if (this.builderService.isAiProviderUnavailable(response)) {
          this.isLoading = false;
          this.errorMessage = AI_UNAVAILABLE_MESSAGE;
          this.failTurn(AI_UNAVAILABLE_MESSAGE);
          this.cdr.detectChanges();
          return;
        }

        try {
          // workingVersion is untouched by applyDraftToWorkingVersion (it clones internally), so it
          // is a valid undo point. Capture it before swapping in the new version.
          const result = await this.data.applyDraftToWorkingVersion(this.workingVersion, response.draft, mode);
          this.undoStack.push(this.workingVersion);
          this.workingVersion = result.version;
          this.previewVersion = JSON.parse(JSON.stringify(result.version));
          this.lastAppliedDraft = response.draft;

          this.conversationHistory.push({ role: 'assistant', text: JSON.stringify(response.draft) });
          const reply = (response.assistantMessage || response.draft.assistantMessage || '').trim()
            || 'Done — the preview is updated. Tell me what to change next.';
          this.turns.push({ role: 'assistant', text: reply });

          this.captureReviewNotes(response, result.applyWarnings);
          this.flashChanges(response.draft);
        } catch (error) {
          console.error('Failed applying AI turn to working version', error);
          this.errorMessage = 'Failed to apply the generated changes.';
        } finally {
          // The await above may have resolved after the dialog closed — don't touch a dead view.
          if (!this.destroyed) {
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        }
      },
      (err) => {
        if (this.destroyed) return;
        this.stopProgress();
        this.isLoading = false;
        // The raw error carries the diagnostic detail; only a user-safe message reaches the UI.
        console.error('AI form draft request failed', err);
        if (err?.status === 429) {
          // Rate limited — the backend supplies a friendly message; compose one if absent.
          const seconds = Number(err?.error?.retryAfterSeconds) || 0;
          this.errorMessage = err?.error?.errorMessage
            || `You've reached the AI form limit.${seconds > 0 ? ' Please try again in ' + this.formatRetryWait(seconds) + '.' : ' Please try again later.'}`;
        } else {
          // Never surface HttpErrorResponse.message — it renders as
          // "Http failure response for /api/forms/ai/generate-form-draft: 0 Unknown Error".
          this.errorMessage = describeAiError(err);
        }
        this.failTurn(this.errorMessage);
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Records a turn that produced no changes, and returns the dialog to a state the user can retry
   * from. `errorMessage` is already set by the caller and stays on screen regardless.
   *
   * On a failed FIRST import the turn is rolled back out of the transcript entirely, because
   * `showPdfCard` and `composerVisible` are keyed on `turns.length === 0` — a stranded turn replaces
   * the upload card with the chat composer, so the "please try again" the user was just told to do
   * becomes impossible without reopening the Studio. Once a draft exists (undoStack is non-empty)
   * the composer is the right place to retry from, so the exchange is kept for context.
   *
   * The user turn is always dropped from `conversationHistory`: an unanswered user message would
   * otherwise be replayed to the model on the next request.
   */
  private failTurn(message: string): void {
    const rollBackToPdfCard = this.data.entry === 'pdf' && this.undoStack.length === 0;

    if (this.conversationHistory.length &&
      this.conversationHistory[this.conversationHistory.length - 1].role === 'user') {
      this.conversationHistory.pop();
    }

    if (rollBackToPdfCard) {
      if (this.turns.length && this.turns[this.turns.length - 1].role === 'user') {
        this.turns.pop();
      }
      return;
    }

    this.turns.push({ role: 'assistant', text: message });
  }

  get hasReviewNotes(): boolean {
    return this.reviewWarnings.length > 0 || this.reviewUnmappedTerms.length > 0;
  }

  get reviewNoteCount(): number {
    return this.reviewWarnings.length + this.reviewUnmappedTerms.length;
  }

  toggleReviewNotes(): void {
    this.reviewNotesExpanded = !this.reviewNotesExpanded;
  }

  private clearReviewNotes(): void {
    this.reviewWarnings = [];
    this.reviewUnmappedTerms = [];
    this.reviewNotesExpanded = true;
  }

  /**
   * Records the review notes for the applied turn and calls them out in the transcript: the
   * backend's own warnings, plus `applyWarnings` raised while the draft was applied locally.
   *
   * The provider-unavailable sentinel and its "Provider detail:" companion are dropped — that path
   * never reaches here (isAiProviderUnavailable short-circuits it above) and both are diagnostic
   * rather than user-facing. Everything else is a change the user needs to know about: the
   * over-20-page truncation notice, control types that fell back, valid values that were
   * renormalized, and PHI-shaped values that were scrubbed out of the draft.
   */
  private captureReviewNotes(response: AiGenerateFormDraftResponse, applyWarnings: string[] = []): void {
    // Server-side warnings and apply-time warnings are two halves of the same story: what the AI
    // could not honour, and what applying its draft could not honour. They share one list so the
    // user reads them in one place. Apply warnings go last — they describe the form as it now
    // stands, so they are the more actionable half to leave closest to the editor.
    this.reviewWarnings = [...(response.warnings || []), ...applyWarnings]
      .map(warning => (warning || '').trim())
      .filter(warning => !!warning
        && !warning.includes(AI_PROVIDER_UNAVAILABLE_WARNING)
        && !warning.startsWith('Provider detail:'));

    this.reviewUnmappedTerms = (response.unmappedTerms || [])
      .map(term => (term || '').trim())
      .filter(term => !!term);

    this.reviewNotesExpanded = true;

    // A note saying content was dropped is not the same kind of note as "this control fell back to
    // a text box" — the draft on screen is missing part of the source. Raise it to the banner too,
    // so it is visible without expanding the panel and reading down a long list.
    const contentLoss = this.reviewWarnings.find(warning =>
      CONTENT_LOSS_WARNING_MARKERS.some(marker => warning.toLowerCase().includes(marker)));
    if (contentLoss) {
      this.noticeMessage = contentLoss;
    }

    if (this.hasReviewNotes) {
      const count = this.reviewNoteCount;
      this.turns.push({
        role: 'assistant',
        text: `Heads up — ${count} item${count === 1 ? '' : 's'} need${count === 1 ? 's' : ''} your review. See "Review notes" below.`
      });
    }
  }

  /**
   * Abandons the in-flight turn at the user's request. The job keeps running server-side (it is
   * fire-and-forget), but the dialog stops polling and returns to an idle, usable state.
   *
   * The abandoned turn is rolled back out of both the transcript and the model-facing history: it
   * produced nothing, so leaving it in `conversationHistory` would feed the next request a user
   * message with no assistant reply. Rolling `turns` back matters just as much — `showPdfCard` and
   * `formTypeRequired` are keyed on `turns.length === 0`, so a stranded turn would hide the upload
   * card and leave the user with no way to retry the import. The notice goes to `noticeMessage`
   * instead of a chat bubble for the same reason.
   */
  stopGenerating(): void {
    if (!this.isLoading) return;

    this.draftSub?.unsubscribe();
    this.draftSub = null;
    this.stopProgress();
    this.isLoading = false;

    if (this.turns.length && this.turns[this.turns.length - 1].role === 'user') {
      this.turns.pop();
    }
    if (this.conversationHistory.length &&
      this.conversationHistory[this.conversationHistory.length - 1].role === 'user') {
      this.conversationHistory.pop();
    }

    this.noticeMessage = 'Generation stopped. Nothing was changed — send your request again when you\'re ready.';
    this.cdr.detectChanges();
  }

  // Turns a Retry-After value (seconds) into a short, human-readable wait.
  private formatRetryWait(seconds: number): string {
    if (seconds >= 90) {
      const minutes = Math.ceil(seconds / 60);
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    const secs = Math.max(1, Math.ceil(seconds));
    return `${secs} second${secs === 1 ? '' : 's'}`;
  }

  undoLast(): void {
    if (!this.canUndo) return;
    const previous = this.undoStack.pop();
    if (!previous) return;

    this.workingVersion = previous;
    this.previewVersion = JSON.parse(JSON.stringify(previous));
    this.popLastExchange();
    this.highlightActive = false;
    this.errorMessage = '';
    this.noticeMessage = '';
    // The notes described the turn being undone, so they no longer apply to what's on screen.
    this.clearReviewNotes();
    this.cdr.detectChanges();
  }

  // Removes the trailing assistant + user turn from both the visible transcript and the
  // model-facing history so an undone turn no longer influences the next request.
  //
  // The visible transcript can carry MORE than one assistant turn per exchange — a turn that
  // produced review notes appends a "Heads up" callout after the reply — so every trailing
  // assistant turn is dropped before the user turn. The model-facing history never doubles up
  // (only the draft JSON is recorded there), so it keeps the single-pop form.
  private popLastExchange(): void {
    while (this.turns.length && this.turns[this.turns.length - 1].role === 'assistant') {
      this.turns.pop();
    }
    if (this.turns.length && this.turns[this.turns.length - 1].role === 'user') {
      this.turns.pop();
    }
    if (this.conversationHistory.length && this.conversationHistory[this.conversationHistory.length - 1].role === 'assistant') {
      this.conversationHistory.pop();
    }
    if (this.conversationHistory.length && this.conversationHistory[this.conversationHistory.length - 1].role === 'user') {
      this.conversationHistory.pop();
    }
  }

  private flashChanges(draft: AiGeneratedFormDraft): void {
    this.changedParagraphNames = (draft.paragraphs || [])
      .map(p => (p.paragraphName || '').trim())
      .filter(name => !!name);
    this.deletedCount = (draft.deleteParagraphIndexes || []).length;
    this.highlightActive = this.changedParagraphNames.length > 0 || this.deletedCount > 0;

    if (this.highlightTimer !== null) {
      clearTimeout(this.highlightTimer);
    }
    this.highlightTimer = setTimeout(() => {
      this.highlightActive = false;
      this.highlightTimer = null;
      this.cdr.detectChanges();
    }, 2500);
  }

  accept(): void {
    if (this.isLoading) return;
    if (!this.hasContent) {
      this.errorMessage = 'Generate or import a form before accepting.';
      return;
    }
    this.dialogRef.close({
      applied: true,
      workingVersion: this.workingVersion,
      appliedDraft: this.lastAppliedDraft
    });
  }

  cancel(): void {
    this.dialogRef.close({ applied: false });
  }

  private startProgress(mode: 'pdf' | 'edit'): void {
    const stages = mode === 'pdf'
      ? FormsBuilderAiStudioDialogComponent.PDF_PROGRESS_STAGES
      : FormsBuilderAiStudioDialogComponent.EDIT_PROGRESS_STAGES;
    this.elapsedSeconds = 0;
    this.progressMessage = stages[0].message;
    this.progressInterval = setInterval(() => {
      this.elapsedSeconds++;
      const current = [...stages].reverse().find(s => this.elapsedSeconds >= s.afterSeconds);
      if (current) {
        this.progressMessage = current.message;
      }
      this.cdr.detectChanges();
    }, 1000);
  }

  private stopProgress(): void {
    if (this.progressInterval !== null) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
    this.progressMessage = '';
    this.elapsedSeconds = 0;
  }
}
